"use client";

import { useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { usersApi } from "@/lib/api/users";
import { authApi, toAuthUser } from "@/lib/api/auth";
import { useAuthStore } from "@/stores/auth-store";
import { useTranslations } from "@/i18n/useTranslations";
import { Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";

interface ChangeNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangeNameDialog({ open, onOpenChange }: ChangeNameDialogProps) {
  const t = useTranslations("nameSetup");
  const tCommon = useTranslations("common");
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const schema = useMemo(
    () =>
      z.object({
        displayName: z.string().min(1, t("displayNameRequired")).max(100),
      }),
    [t],
  );

  type FormData = z.infer<typeof schema>;

  const form = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema) as any,
    defaultValues: { displayName: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ displayName: user?.displayName || "" });
    }
  }, [open, user?.displayName, form]);

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      usersApi.updateDisplayName(user!.id, { displayName: data.displayName.trim() }),
    onSuccess: async () => {
      const freshUser = await authApi.me();
      setUser(toAuthUser(freshUser));
      toast.success(t("success"));
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("error"));
    },
  });

  const onSubmit = (data: FormData) => mutation.mutate(data);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset();
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-primary" />
            {t("changeNameTitle")}
          </DialogTitle>
          <DialogDescription>{t("changeNameDesc")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form id="change-name-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("displayNameLabel")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("displayNamePlaceholder")} {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {tCommon("cancel")}
          </Button>
          <Button type="submit" form="change-name-form" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {tCommon("loading")}
              </>
            ) : (
              tCommon("submit")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
